import {
  createFrontendFeatureLoader,
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { createElement } from 'react';

import type { RemoteConfig, RemoteLoader, RemoteType } from './types';
import { loadNextjsRemote } from './nextjs';
import { loadStandardRemote } from './standard-loader';
import { resolveRemoteOrigin } from './utils';

const loaderRegistry: Record<RemoteType, RemoteLoader> = {
  nextjs: loadNextjsRemote,
  standard: loadStandardRemote,
};

const validRemoteTypes = new Set<string>(Object.keys(loaderRegistry));

const isValidModuleFederationConfig = ({
  name,
  entry,
  type,
  index,
}: {
  name: string;
  entry: string;
  type: string;
  index: number;
}) => {
  if (!validRemoteTypes.has(type) || !entry.trim() || !URL.canParse(entry)) {
    // eslint-disable-next-line no-console
    console.error(
      `[MF] Remote "${name}" (index ${index}) has an invalid configuration. ` +
        `Please review the configuration and ensure all required fields are correct.`,
    );
    return false;
  }

  const parsedEntry = new URL(entry);
  const allowedProtocols = ['https:', 'http:'];
  if (!allowedProtocols.includes(parsedEntry.protocol)) {
    // eslint-disable-next-line no-console
    console.error(
      `[MF] Remote "${name}" (index ${index}) uses a disallowed protocol "${parsedEntry.protocol}" in entry URL: "${entry}". ` +
        `Only ${allowedProtocols.join(', ')} are permitted.`,
    );
    return false;
  }
  return true;
};

const parseRemoteConfigs = (
  config: Parameters<
    Parameters<typeof createFrontendFeatureLoader>[0]['loader']
  >[0]['config'],
): RemoteConfig[] => {
  return (
    config
      .getOptionalConfigArray('app.moduleFederation.remotes')
      ?.flatMap<RemoteConfig>((remote, index) => {
        try {
          const name = remote.getString('name');
          const entry = remote.getString('entry');
          const exposedModule =
            remote.getOptionalString('exposedModule') ?? 'App';
          const path = remote.getString('path');
          const title = remote.getOptionalString('title') ?? name;
          const noHeader = remote.getOptionalBoolean('noHeader') ?? false;
          const type = (remote.getOptionalString('type') ??
            'standard') as RemoteType;

          const validConfig = isValidModuleFederationConfig({
            name,
            entry,
            type,
            index,
          });
          if (!validConfig) {
            return [];
          }

          return [{ name, entry, exposedModule, path, title, noHeader, type }];
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            `[MF] Remote at index ${index} has an invalid configuration and will be skipped.`,
            err,
          );
          return [];
        }
      }) ?? []
  );
};

const createRemotePage = (remote: RemoteConfig) => {
  const remoteOrigin = resolveRemoteOrigin(remote);

  return PageBlueprint.make({
    name: remote.name,
    params: {
      noHeader: remote.noHeader,
      title: remote.title,
      path: remote.path,
      loader: async () => {
        const module = await loaderRegistry[remote.type](remote, remoteOrigin);

        return createElement(module, {
          apiBaseUrl: remoteOrigin,
          basePath: remote.path,
        } as Record<string, unknown>);
      },
    },
  });
};

export const moduleFederationLoader = createFrontendFeatureLoader({
  async loader({ config }) {
    const remotes = parseRemoteConfigs(config);
    if (!remotes.length) return [];

    return [
      createFrontendModule({
        pluginId: 'app',
        extensions: remotes.map(remote => createRemotePage(remote)),
      }),
    ];
  },
});
